import React from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Home, ArrowLeft, Bot } from "lucide-react";
import { pagePath } from "../lib/routes";

/**
 * Friendly 404 page shown for any URL that doesn't match a defined route
 * (including legacy WordPress paths that a legacy Google index still points to).
 *
 * Sets <meta name="robots" content="noindex"> so search engines drop these
 * unknown URLs instead of treating them as "soft 404" duplicate homepage.
 */
export default function NotFound({ lang }) {
  const copy = {
    et: {
      badge: "404",
      title: "Lehte ei leitud",
      body:
        "Sinu otsitud lehte ei ole (enam) meie kodulehel. Ehk aitab meie AI Assistent või liigume avalehele.",
      cta_home: "Ava avaleht",
      cta_services: "Vaata teenuseid",
      cta_ai: "Küsi AI Assistendilt",
    },
    ru: {
      badge: "404",
      title: "Страница не найдена",
      body:
        "Запрошенной страницы (больше) нет на нашем сайте. Возможно, вам поможет наш AI-помощник или перейдите на главную.",
      cta_home: "На главную",
      cta_services: "Смотреть услуги",
      cta_ai: "Спросить AI-помощника",
    },
    en: {
      badge: "404",
      title: "Page not found",
      body:
        "The page you're looking for is no longer here. Try our AI Assistant or head back to the homepage.",
      cta_home: "Go to homepage",
      cta_services: "See services",
      cta_ai: "Ask the AI Assistant",
    },
  };
  const c = copy[lang] || copy.et;

  const openAI = () => {
    // AIAssistant listens for this custom event via its FAB (see AIAssistant.jsx).
    // If not wired, users can still click the floating button.
    window.dispatchEvent(new CustomEvent("open-ai-assistant"));
  };

  return (
    <div className="container-page py-24 sm:py-32 flex flex-col items-center text-center" data-testid="not-found-page">
      <Helmet>
        <html lang={lang || "et"} />
        <title>{`${c.title} — Tallinna Endokrinoloogia Kliinik`}</title>
        <meta name="robots" content="noindex,follow" />
      </Helmet>

      <div className="text-[13px] font-semibold uppercase tracking-widest text-teal-500 mb-3">
        {c.badge}
      </div>
      <h1 className="text-4xl sm:text-5xl font-semibold text-surface-ink mb-4">
        {c.title}
      </h1>
      <p className="text-[16px] text-surface-inkSoft max-w-xl leading-relaxed mb-8">
        {c.body}
      </p>

      <div className="flex flex-wrap gap-3 justify-center">
        <Link
          to={pagePath("home", lang)}
          data-testid="not-found-home-link"
          className="btn-primary inline-flex items-center gap-2"
        >
          <Home className="h-4 w-4" /> {c.cta_home}
        </Link>
        <Link
          to={pagePath("services", lang)}
          data-testid="not-found-services-link"
          className="btn-secondary inline-flex items-center gap-2"
        >
          <ArrowLeft className="h-4 w-4" /> {c.cta_services}
        </Link>
        <button
          type="button"
          onClick={openAI}
          data-testid="not-found-ai-btn"
          className="btn-secondary inline-flex items-center gap-2"
        >
          <Bot className="h-4 w-4" /> {c.cta_ai}
        </button>
      </div>
    </div>
  );
}
